import React, {useState} from 'react';
import {validationEmail} from '../../global/validate';
import './index.scss';
import {useDispatch} from 'react-redux';
import {setLoader} from '../../store/builder/reducer';
import {setUser} from '../../store/builder/loginSlice';
import {Link, useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import {Helmet} from 'react-helmet';
import {useTranslation} from '../../hooks/useTranslation';
import LanguageSelector from '../../components/LanguageSelector';
import {useCheckEmailMutation, useLoginMutation} from '../../hooks/useAuthLogin';
import {getPostLoginPath} from '../../utils/postLoginNavigation';
import {
  setCurrentFbo,
  setUserToken,
  setOutOfServiceModalStatus,
} from '../../utils/tokenHandler';

export default function Login() {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {mutate: loginMutate} = useLoginMutation();
  const {mutate: checkEmailMutate} = useCheckEmailMutation();
  const [deactivatedStatus, setDeactivatedStatus] = useState(false);
  const [ssoOnlyStatus, setSsoOnlyStatus] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [state, setState] = useState({
    email: {
      id: 'email',
      value: null,
      errorShow: false,
    },
    password: {
      id: 'password',
      value: null,
      errorShow: false,
    },
  });

  const [eye, setEye] = useState(false);

  const inputHandler = e => {
    setState(old => ({
      ...old,
      [e.target.id]: {...old[e.target.id], value: e.target.value},
    }));
  };

  const handleAuthError = err => {
    if (err?.response?.status === 423) {
      setDeactivatedStatus(true);
    } else if (err?.response?.status === 419) {
      setSsoOnlyStatus(true);
    } else {
      toast.error(err?.response?.data?.message || t('messages.wentWrong'), {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 5000,
      });
    }
  };

  const submitHandler = e => {
    e.preventDefault();
    if (state?.email?.value && validationEmail(state?.email?.value)) {
      if (showPassword) {
        loginMutate(
          {email: state.email.value, password: state.password.value},
          {
            onMutate: () => dispatch(setLoader(true)),
            onSuccess: apiData => {
              dispatch(setUser(apiData.data));
              toast.success(apiData.message, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 5000,
              });
              setUserToken(apiData.access_token);
              const d = apiData?.data;
              setCurrentFbo(d?.agency?.id ?? null);
              setOutOfServiceModalStatus('show');
              navigate(getPostLoginPath(d));
            },
            onError: handleAuthError,
            onSettled: () => dispatch(setLoader(false)),
          },
        );
      } else {
        checkEmailMutate(state.email.value, {
          onMutate: () => dispatch(setLoader(true)),
          onSuccess: data => {
            if (data?.data?.link) {
              window.location.href = data.data.link;
            } else {
              setShowPassword(true);
            }
          },
          onError: handleAuthError,
          onSettled: () => dispatch(setLoader(false)),
        });
      }
    } else {
      setState(old => ({
        ...old,
        email: {...old.email, errorShow: true},
        password: {...old.password, errorShow: false},
      }));
    }
  };

  const renderDeactivated = () => {
    return (
      <div className='modal'>
        <div className='modal__content'>
          <div className='modal__content-head no-bottom'>
            <img
              className='modal__content-exit'
              src='/assets/icons/close.svg'
              alt='close'
              onClick={() => setDeactivatedStatus(false)}
            />
          </div>
          <div className='modal__content-body'>
            <img
              className='deactivated-img'
              src='/assets/rafiki.png'
              alt='bg'
            />
            <p className='deactivated'>
              {t('login.deactivatedMessage', { email: state?.email?.value })}
            </p>
          </div>
          <div className='modal__content-bottom'>
            <button className='save full'>
              <a target='_blank' href='https://www.ndxcentral.com/#contact'>
                {t('login.contactNDX')}
              </a>
            </button>
          </div>
        </div>
        <div className='modal__bg'></div>
      </div>
    );
  };

  const renderSsoOnly = () => {
    return (
      <div className='modal'>
        <div className='modal__content'>
          <div className='modal__content-head no-bottom'>
            <img
              className='modal__content-exit'
              src='/assets/icons/close.svg'
              alt='close'
              onClick={() => setSsoOnlyStatus(false)}
            />
          </div>
          <div className='modal__content-body'>
            <img
              className='deactivated-img'
              src='/assets/rafiki.png'
              alt='bg'
            />
            <p className='deactivated'>
              {t('login.ssoOnlyMessage')}
            </p>
          </div>
          <div className='modal__content-bottom'>
            <button className='save full' onClick={() => setSsoOnlyStatus(false)}>
              {t('actions.close')}
            </button>
          </div>
        </div>
        <div className='modal__bg'></div>
      </div>
    );
  };

  return (
    <div className='auth'>
      <Helmet>
        <title>{t('login.pageTitle')}</title>
      </Helmet>
      {deactivatedStatus && renderDeactivated()}
      {ssoOnlyStatus && renderSsoOnly()}
      <div className='auth__language-selector'>
        <LanguageSelector />
      </div>
      <div className='auth__wrapper'>
        <div className='auth__left'>
          <img src='/assets/auth-image.png' alt='bg' />
        </div>
        <div className='auth__right'>
          <div className='auth__content'>
            <div className="auth__logo-row">
              <div className="auth__divider"></div>
              <img src='/assets/icons/login-logo.svg' alt='logo' className="auth__logo-img" />
              <div className="auth__divider"></div>
            </div>
            <h1 className='auth__title'>{t('login.title')}</h1>

            <form className='auth__form' onSubmit={submitHandler}>
              <>
                {showPassword ? (
                  <div className='auth__emailText'>
                    {t('login.signingInWith')} <span>{state?.email?.value}</span>
                  </div>
                ) : (
                  <div className='auth__field'>
                    <label htmlFor='email' className='auth__label'>
                      {t('login.email')}
                    </label>
                    <input
                      id='email'
                                              placeholder={t('login.emailPlaceholder')}
                      type='text'
                      className='auth__input'
                      onChange={inputHandler}
                      autoFocus={true}
                    />
                    {state?.email?.errorShow && (
                      <span className='auth__error'>{t('login.emailError')}</span>
                    )}
                  </div>
                )}
              </>
              <>
                {showPassword && (
                  <>
                    <div className='auth__field'>
                      <label htmlFor='password' className='auth__label'>
                        {t('login.password')}
                      </label>
                      <input
                                                  placeholder={t('login.passwordPlaceholder')}
                        id='password'
                        type={eye ? 'text' : 'password'}
                        className='auth__input'
                        onChange={inputHandler}
                        autoFocus={true}
                      />
                      <img
                        className='eye'
                        src={
                          eye
                            ? '/assets/icons/eye-off.svg'
                            : '/assets/icons/eye.svg'
                        }
                        alt='eye'
                        onClick={() => {
                          setEye(!eye);
                        }}
                      />
                      {state?.password?.errorShow && (
                        <span className='auth__error'>
                          {t('login.passwordError')}
                        </span>
                      )}
                    </div>
                    <div className='auth__links'>
                      <div className='remember'>
                        <div className='auth__checkbox'>
                          <input type='checkbox' id='remember' />
                          <label htmlFor='remember'>{t('login.rememberMe')}</label>
                        </div>
                      </div>
                      <Link to='/forgot-password'>{t('login.forgotPassword')}</Link>
                    </div>
                  </>
                )}
              </>
              <button className='auth__submit'>{t('actions.continue')}</button>
              <div className='auth__other'>
                <span>{t('login.noAccount')}</span>
                <a href='https://ndxcentral.com/contact' target='_blank'>
                                      {t('login.signUp')}
                </a>
              </div>
              <div className='auth__terms'>
                <a href='https://ndxcentral.com/privacy-policy' target='_blank'>{t('login.termsOfUse')}</a>
                                  <a href='https://ndxcentral.com/privacy-policy' target='_blank'>{t('login.privacyPolicy')}</a>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
